import { useMemo, useRef, useState } from "react";
import { termsReady } from "../lib/termsOfUse";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { startRegistration } from "@simplewebauthn/browser";
import { Check, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { safeRedirectTarget } from "../lib/redirectTarget";
import { useFocusFirstInvalid } from "../lib/focusFirstInvalid";
import { homePathForRole } from "../lib/homePath";
import { arrivalToastSuppressed } from "../lib/arrivalToastGate";
import { USAGE_DISCLOSURE_ONE_LINE } from "../lib/usageDisclosure";
import { GUARDIAN_HELD_BELOW_AGE, ageFromDob } from "../lib/dateOfBirth";
import { MIN_PASSWORD } from "../lib/passwordRules";
import { TopBar, Field, GuardianPanel, ActionButton, TextLink } from "../components/kit";
import { TAP_INLINE_LINK_STYLE, TAP_LINK_STYLE, tapLine } from "../components/kit/tapTarget";

/**
 * SignUpNew — issue #753, the sign-up screen redrawn to the approved
 * `SignUp.dc.html` (design-baseline-round-4). Lives behind
 * `newUi("signup")` (`App.js`); `Register.jsx`, the shipped screen, is
 * untouched and keeps serving `/register` with the flag off.
 *
 * This is presentation and three approved wording/behaviour changes
 * (#724, #725, #740) on top of the SAME data model and the SAME API —
 * nothing here talks to a different endpoint or widens what the server
 * accepts. `GUARDIAN_HELD_BELOW_AGE`, `MIN_PASSWORD` and `ageFromDob` are
 * imported from `lib/dateOfBirth.js` and `lib/passwordRules.js` rather
 * than redeclared, so the two screens cannot silently disagree about the one number an API test
 * (`api/test/guardianConfirmation.test.ts`) reads by path.
 */

/**
 * A single "I am a" tick — Player or Coach. Not the shadcn `Checkbox`
 * (that remains Register.jsx's own choice): the drawing's 22px square with
 * a navy fill and a checkmark is the same visual `GuardianPanel`'s own
 * confirmation tick already renders, so this mirrors that button pattern
 * rather than introducing a second checkbox idiom into the kit.
 */
function TickRow({ testId, label, checked, onChange, disabled = false }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={() => !disabled && onChange(!checked)}
      aria-pressed={checked}
      disabled={disabled}
      className="flex items-center"
      style={{
        gap: 10,
        minHeight: 44,
        width: "100%",
        background: "none",
        border: "none",
        padding: 0,
        textAlign: "left",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span
        aria-hidden="true"
        className="flex items-center justify-center"
        style={{
          width: 22,
          height: 22,
          borderRadius: "var(--nw-radius-check)",
          border: checked ? "none" : "2px solid var(--nw-line-strong)",
          background: checked ? "var(--nw-navy)" : "var(--nw-surface)",
          flexShrink: 0,
        }}
      >
        {checked && <Check size={14} style={{ color: "#FFFFFF" }} />}
      </span>
      <span style={{ fontSize: 15, fontWeight: checked ? 600 : 400, color: "var(--nw-ink)" }}>{label}</span>
    </button>
  );
}

/** The one reason the server gives for an address that already has an
 * account (`api/src/routes/auth.ts`, both the pre-check and the unique-index
 * race). Matched only to offer a next step beside it — the words shown are
 * always the server's own, never a different string. */
const EMAIL_TAKEN_DETAIL = "Email already registered";
const SIGN_IN_PATH = "/login";

/**
 * #1183 — the drawn "when the account could not be created" state
 * (`SignUp.dc.html`, `create-failed`): a #F1F5F9 box with a 3px #B42318 left
 * edge, 13px ink text, the server's own words, a message that stays rather
 * than a toast (D48/D57). The drawing has no "already registered" variant, so
 * that case reuses this same box and adds its next step as plain links
 * underneath; the warning icon and weight 600 are the drawn failure-message
 * rule (`settings-display-name-error`, #1131).
 */
// #1392 review: the two links in the "Email already registered" sentence. The inline tap style gives 43.3px
// at this 13px size, so the padding is a little deeper, and sideways too because "Sign in" is 40px wide
// (the sideways padding is cancelled by an equal negative margin, so no word moves).
const ALREADY_REGISTERED_LINK_BOX = {
  ...TAP_INLINE_LINK_STYLE,
  paddingBlock: "calc(var(--nw-size-tap) / 2.8)",
  paddingInline: "calc(var(--nw-size-tap) / 2)",
  marginInline: "calc(var(--nw-size-tap) / -2)",
};

function CreateFailed({ message, state }) {
  return (
    <div
      role="alert"
      data-testid="register-error"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        padding: "10px 12px",
        background: "#F1F5F9",
        borderLeft: "3px solid #B42318",
        borderRadius: 10,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 6, fontSize: 13, lineHeight: 1.5, fontWeight: 600, color: "#0F172A" }}>
        <span style={{ display: "inline-flex", flexShrink: 0, marginTop: 2 }}>
          <TriangleAlert size={15} color="#B42318" aria-hidden="true" />
        </span>
        <span data-testid="register-error-message">{message}</span>
      </div>
      {message === EMAIL_TAKEN_DETAIL && (
        <div className="nw-help" data-testid="register-error-next">
          <Link to={SIGN_IN_PATH} state={state} style={{ ...ALREADY_REGISTERED_LINK_BOX, fontWeight: 700, color: "var(--nw-navy)" }} data-testid="register-error-signin">
            Sign in
          </Link>
          {" with that address, or "}
          <Link to="/forgot-password" data-testid="register-error-reset" style={{ ...ALREADY_REGISTERED_LINK_BOX, fontWeight: 700, color: "var(--nw-navy)" }}>
            reset your password
          </Link>
          .
        </div>
      )}
    </div>
  );
}

// #1392 (D221 G4): "Sign in" under the form gets a 44px tap box around the unchanged label. It sits one
// stack gap (14px) under the passkey door, whose own box reaches 12px below its label, so a symmetric
// box would cover the lower part of that one. This box grows downward only: the label stays at the top
// of it and the margin box stays one line.
const SIGN_IN_LINK_BOX = { ...TAP_LINK_STYLE, alignItems: "flex-start", marginBlock: `0 calc(${tapLine()} - var(--nw-size-tap))` };

export default function SignUpNew() {
  const { user, registerResult, completePasskeySignUpResult } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  // Same #257 deep-link carry and the same #714 role-aware fallback as
  // Register.jsx — this screen creates the same kind of account, so the
  // "where does a signed-in visitor land" question has the same answer.
  const target = safeRedirectTarget(location.state?.from, homePathForRole(user?.role));

  const [form, setForm] = useState({
    display_name: "",
    email: "",
    password: "",
    date_of_birth: "",
  });
  // #724: two independent ticks, not one either-or Select. Both start
  // unticked — "neither ticked" is a valid state (resolves to the schema
  // default, player) rather than a required field this screen invents.
  const [playerTicked, setPlayerTicked] = useState(false);
  const [coachTicked, setCoachTicked] = useState(false);
  const [guardianConfirmed, setGuardianConfirmed] = useState(false);
  const [dobError, setDobError] = useState("");
  // #1306 (D303, D317): the passkey door names a missing name or email on the
  // field itself, not in a toast. Keyed by form field; a value is the sentence.
  const [doorErrors, setDoorErrors] = useState({});
  // A refused Save moves the player to the first marked field (D303). `attempt`
  // counts refusals, so pressing the button again with the same mark still on
  // is a new error set and takes the player back to it (NewGoalSheet's shape).
  // The root is the whole `<form>`: the passkey button sits outside it, but the
  // fields it marks are inside.
  const formRef = useRef(null);
  const [attempt, setAttempt] = useState(0);
  const focusErrors = useMemo(
    () => ({
      name: doorErrors.display_name ? attempt : 0,
      dob: dobError ? attempt : 0,
      email: doorErrors.email ? attempt : 0,
    }),
    [doorErrors, dobError, attempt],
  );
  const armFocus = useFocusFirstInvalid(formRef, focusErrors);
  const [busy, setBusy] = useState(false);
  // #1183: the server's reason for a failed create, shown on the page (D57).
  const [createError, setCreateError] = useState("");
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const passkeySupported = typeof window !== "undefined" && !!window.PublicKeyCredential;

  const age = ageFromDob(form.date_of_birth);
  const guardianHeld = age !== null && age < GUARDIAN_HELD_BELOW_AGE;
  const isJunior = age !== null && age < 18;

  // #740 item 3 / M-09: an account for a player under 11 is a player
  // account, and Coach is refused CLIENT-SIDE rather than left to the
  // server's `coach_under_guardian_age` 400. `coachAllowed` gates both the
  // tick's own disabled state and the role that gets posted, so the two
  // can never disagree — a stale `coachTicked=true` from before the date
  // of birth was edited into the band simply stops being honoured, rather
  // than needing an effect to go and unset it.
  const coachAllowed = !guardianHeld;
  const coachChecked = coachTicked && coachAllowed;
  const bothTicked = playerTicked && coachChecked;
  // `role` — single-valued, and stays exactly this for every client that
  // predates #941 (including an older server, which reads only this key).
  // Player only -> player. Coach only -> coach. Neither, or both -> player
  // (D36: a both-roles account's first session is the player hat).
  const resolvedRole = coachChecked && !playerTicked ? "coach" : "player";
  // Issue #941 — `roles`, the wire that lets ticking BOTH actually grant
  // both (D100/D42), closing the gap the sign-up screen used to leave open:
  // until now, ticking both silently created a player-only account.
  // Player only -> ["player"]. Coach only -> ["coach"]. Both -> the two
  // together. Neither ticked -> ["player"], the same default `resolvedRole`
  // above falls back to. `role` above is sent alongside this on every
  // request (see `signupPayload`) so an older API that has never heard of
  // `roles` still gets a valid, single-valued body.
  const resolvedRoles = bothTicked ? ["player", "coach"] : coachChecked ? ["coach"] : ["player"];

  if (user) return <Navigate to={target} replace />;

  const dobLooksWrong = () => {
    if (age === null) {
      setDobError("Enter the player's date of birth as a real date in the past.");
      return true;
    }
    if (age > 120) {
      setDobError("That date of birth doesn't look right — please check it.");
      return true;
    }
    setDobError("");
    return false;
  };

  /** The body both doors post, minus the credential each uses — same shape
   * `Register.jsx`'s `signupPayload` sends, with `role` resolved from the
   * two ticks instead of read off a `Select`.
   *
   * Issue #941: `roles` rides alongside `role`, never instead of it —
   * `role` stays the single-valued field every server (old or new) can
   * read, and `roles` is what a server that knows about it uses to decide
   * (server-side precedence, `api/src/routes/auth.ts`). Sending both is
   * what lets this screen ship without a version gate on the API. */
  const signupPayload = () => ({
    ...form,
    role: resolvedRole,
    roles: resolvedRoles,
    guardian_confirmed: guardianConfirmed,
  });

  const submit = async (e) => {
    e.preventDefault();
    armFocus();
    if (dobLooksWrong()) {
      setAttempt((n) => n + 1);
      return;
    }
    setBusy(true);
    setCreateError("");
    // #1183: the reason comes back in the RESULT, never from `useAuth().error`
    // — that binding is the render before this request, so it is stale here.
    const result = await registerResult(signupPayload());
    const created = result.ok ? result.user : false;
    setBusy(false);
    if (created) {
      // D109 (PR #846, second review) — see arrivalToastGate.js. Deliberately
      // a second call to `safeRedirectTarget`, not a shared `dest` variable,
      // so `SignUpNew.test.js`'s own pin — "navigates by the role the
      // SERVER created, via homePathForRole" — keeps matching this exact,
      // already-reviewed navigation line unchanged.
      if (!arrivalToastSuppressed(safeRedirectTarget(location.state?.from, homePathForRole(created.role))))
        toast.success("Account created!");
      nav(safeRedirectTarget(location.state?.from, homePathForRole(created.role)), { replace: true });
    } else setCreateError(result.error || "Registration failed");
  };

  // #714's passkey door never carries a role — `passkeys.ts`'s
  // `signupFieldsSchema` omits it and hard-codes `role: "player"` at the
  // create. With two ticks that hard-code is now WRONG whenever the
  // resolved role is Coach, so this door is hidden for that case (below)
  // rather than silently creating a player for someone who asked to be a
  // coach. This function is unreachable in that state — the button that
  // calls it does not render — but the same `dobLooksWrong` gate stays,
  // matching the password door.
  const signUpWithPasskey = async () => {
    // #1306 (D303, D317): every refusal is drawn on its field at once, then
    // focus goes to the first marked one in page order (name, date of birth,
    // email). No toast: the marks are the message.
    armFocus();
    const dobRefused = dobLooksWrong();
    const missing = {
      display_name: form.display_name.trim() ? null : "Player's full name is required",
      email: form.email.trim()
        ? null
        : guardianHeld
          ? "Parent or guardian's email address is required"
          : "Email is required",
    };
    setDoorErrors(missing);
    if (dobRefused || missing.display_name || missing.email) {
      setAttempt((n) => n + 1);
      return;
    }
    setPasskeyBusy(true);
    setCreateError("");
    try {
      const fields = {
        email: form.email,
        display_name: form.display_name,
        date_of_birth: form.date_of_birth,
        guardian_confirmed: guardianConfirmed,
      };
      const { data: options } = await api.post("/auth/passkey/register-new/options", fields);
      const ceremony = await startRegistration({ optionsJSON: options });
      const result = await completePasskeySignUpResult(fields, ceremony);
      const created = result.ok ? result.user : false;
      if (created) {
        // D109 (PR #846, second review) — see arrivalToastGate.js.
        if (!arrivalToastSuppressed(target)) toast.success("Account created!");
        nav(target, { replace: true });
      } else {
        setCreateError(result.error || "Registration failed");
      }
    } catch (e) {
      if (e?.name !== "NotAllowedError" && e?.name !== "AbortError") {
        // D57: on the page, not a toast. The options call answers with the
        // same details the password door gets — "Email already registered"
        // included.
        setCreateError(
          formatApiErrorDetail(e.response?.data?.detail) ||
            "Could not create the account with a passkey on this device.",
        );
      }
    }
    setPasskeyBusy(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--nw-canvas, #F8FAFC)" }}>
      {/* #806 fix round 2, F5 — the marker every other redrawn screen
          carries (OnboardingNew.jsx:130-139, CoachSetUp.jsx,
          CoachPortalEmptyState.jsx), missing here until now. Its absence
          is what let a marker-SCOPED check (this file's own
          `frontend/e2e/responsive.spec.js` clipping check) measure nothing
          on `/register` even with the flag on — confirmed directly: adding
          this one line locally moved `/register` from unmeasured to
          checked, and it passed (the date field there was never narrow).
          `EXPECTED_NEW_SCREEN_ROUTES` in that file now hard-fails if this
          line is ever removed while `newUi("signup")` stays on. */}
      <span data-testid="signup-new-screen" className="sr-only" aria-hidden="true" />
      {/* Issue #792 — this file never used a rem Tailwind spacing utility
          (every gap here was already a literal px number in an inline
          `style`, so it never moved with the text-size control), but its
          page-stack gap was a bare `14` rather than the token that names
          it — moved onto `var(--nw-space-stack)` for the same reason the
          other three rebuilt screens now reference the token instead of a
          number that happens to match it (SignUp.dc.html:61 draws `gap:
          14px`). */}
      <div
        className="nw-column"
        style={{
          padding: 20,
          display: "flex",
          flexDirection: "column",
          gap: "var(--nw-space-stack)",
        }}
      >
        <TopBar variant="none" data-testid="signup-topbar" />

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <h2 className="nw-title" data-testid="signup-heading">Create your account</h2>
          {/* #725: the old product-explanation line and its landing-page
              link (`link-landing`) come OFF this screen — that clause and
              the ownership line now live on the landing page (M-07), which
              is not part of this refactor. This is the guardian sentence
              alone, verbatim, per Martin's approval. Interpolated (not the
              literal 11) so it can never drift from the panel below it. */}
          <p className="nw-lede" data-testid="signup-subline">
            Parents or guardians set up and hold accounts for players under {GUARDIAN_HELD_BELOW_AGE}.
          </p>
        </div>

        <form ref={formRef} onSubmit={submit} data-testid="register-form">
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Field
              label="Player's full name"
              id="register-name"
              data-testid="register-name"
              required
              value={form.display_name}
              error={doorErrors.display_name || null}
              onChange={(e) => {
                setForm({ ...form, display_name: e.target.value });
                setDoorErrors((cur) => (cur.display_name ? { ...cur, display_name: null } : cur));
              }}
              placeholder="[Player's full name]"
            />

            {/* #1306 (D303): the kit pattern. Field draws the red border,
                `aria-invalid`, `aria-describedby` and the sentence
                (`register-dob-error`), and the help line only while there is
                no error, so the no-error DOM is `register-dob-help` as before. */}
            <Field
              label="Player's date of birth"
              id="register-dob"
              data-testid="register-dob"
              required
              type="date"
              value={form.date_of_birth}
              error={dobError || null}
              help="This decides whether a parent or guardian holds the sign-in — see below."
              onChange={(e) => {
                setForm({ ...form, date_of_birth: e.target.value });
                setDobError("");
              }}
            />

            {/* #724: two independent ticks, above the email, replacing the
                shipped either-or Select. */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                padding: "var(--nw-space-card)",
                background: "var(--nw-surface)",
                border: "1px solid var(--nw-line)",
                borderRadius: "var(--nw-radius-card)",
              }}
            >
              <div className="nw-label">I am a</div>
              {/* #753 fix round F6: an in-flight passkey ceremony captures
                  `fields` (no role) before `startRegistration` awaits — if
                  the ticks stayed live during that await, un-ticking Player
                  and ticking Coach while the browser's own sheet is open
                  would hide the door behind it while the ceremony still
                  finishes and creates a player. Freezing both ticks for the
                  same busy window the submit/passkey buttons already freeze
                  removes the question rather than racing it. */}
              <TickRow
                testId="register-role-player"
                label="Player"
                checked={playerTicked}
                onChange={setPlayerTicked}
                disabled={busy || passkeyBusy}
              />
              <TickRow
                testId="register-role-coach"
                label="Coach"
                checked={coachChecked}
                onChange={setCoachTicked}
                disabled={!coachAllowed || busy || passkeyBusy}
              />
              <p className="nw-help">Tick either, or both.</p>
              {/* D181 (Andreas, 2026-09-30, issue #941's step 0): the
                  extra sentence that used to render here when both ticks
                  were on is DELETED, not reworded. D100 lifted the
                  #169/#507 hold on a both-roles account and this slice
                  builds it, so that sentence's claim (an account that is
                  both was "coming" and "for now" created a player only)
                  became false the moment this shipped — and D181's own
                  words are "no new words": the line above already says
                  everything true about ticking both, on its own, with
                  nothing appended. */}
              {/* The resolved role, for anything (a journey, a future
                  caller) that needs to read it rather than re-derive it
                  from the two ticks — never rendered as a control. */}
              <input type="hidden" data-testid="register-role" value={resolvedRole} readOnly />
            </div>

            {/* #169's guardian path, unchanged in behaviour from
                Register.jsx: entered by date of birth, not self-declared.
                #753 fix round, Q1 (Andreas's decision, answering #725's
                unraised question): the ownership sentence stays ON this
                panel. The drawing (SignUp.dc.html) took it off following
                M-07, but #725 asked in writing that Andreas be consulted
                before that copy landed and he was not — his call is that
                it stays here too, not instead of the landing page's copy:
                the guardian panel is the one place an adult is told whose
                account it is at the moment they hand over their own email
                address for a child under 11. The drawing is being updated
                to match; the product-explanation line and `link-landing`
                stay off per M-07/M-06, unaffected by this. */}
            {guardianHeld && (
              <GuardianPanel
                data-testid="register-guardian"
                checkboxTestId="register-guardian-confirm"
                eyebrow={`Under ${GUARDIAN_HELD_BELOW_AGE} — a guardian sets this up`}
                prose={
                  <>
                    The account is in the player's name and stays theirs. While they are under{" "}
                    {GUARDIAN_HELD_BELOW_AGE}, a parent or guardian holds the sign-in details: use{" "}
                    <strong>your own email address</strong> below, and set a password you keep.
                  </>
                }
                ruleLine={`An account for a player under ${GUARDIAN_HELD_BELOW_AGE} is a player account. Coach cannot be ticked for it.`}
                checked={guardianConfirmed}
                onCheckedChange={setGuardianConfirmed}
                statement="I am this player's parent or guardian and I am setting the account up for them."
              />
            )}
            {isJunior && !guardianHeld && (
              <div
                className="nw-prose"
                style={{
                  color: "var(--nw-ink-lede)",
                  padding: "var(--nw-space-card)",
                  background: "var(--nw-line-faint)",
                  borderRadius: "var(--nw-radius-card)",
                }}
                data-testid="register-junior-note"
              >
                From {GUARDIAN_HELD_BELOW_AGE} the player holds their own sign-in, so the email and
                password below are theirs. A parent or guardian should know the account exists — while
                the player is a minor, a guardian approves the account and the links it makes.
              </div>
            )}

            <Field
              label={guardianHeld ? "Parent or guardian's email address" : "Email"}
              id="register-email"
              data-testid="register-email"
              required
              type="email"
              value={form.email}
              error={doorErrors.email || null}
              onChange={(e) => {
                setForm({ ...form, email: e.target.value });
                setDoorErrors((cur) => (cur.email ? { ...cur, email: null } : cur));
              }}
            />
            <div>
              <Field
                label="Password"
                data-testid="register-password"
                required
                type="password"
                minLength={MIN_PASSWORD}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                aria-describedby="register-password-help"
              />
              <p id="register-password-help" className="nw-help" style={{ marginTop: 4 }}>At least {MIN_PASSWORD} characters.</p>
            </div>

            {/* #224: disclosure, not consent — plain text, not a checkbox,
                read before the account exists. */}
            {/* D446 (#1626): one line, then a link to the full notice. The
                row is laid out like the privacy-notice row below (#1340), so
                the link's tap box has no text under it when it wraps. */}
            <p
              className="nw-help flex flex-wrap items-center"
              style={{ columnGap: 4, rowGap: 24, marginBottom: 16 }}
              data-testid="register-usage-disclosure"
            >
              <span>{USAGE_DISCLOSURE_ONE_LINE}</span>
              <TextLink as={Link} to="/privacy#usage-counts" state={{ from: location.pathname }} className="underline" data-testid="register-usage-link">
                What we count
              </TextLink>
            </p>

            {/* #449: the privacy-notice link. The drawing omits it; the
                drawing is wrong here — this is `parity`, not a redraw. */}
            {termsReady() ? (
              /* Issue #1340: with the terms link added the sentence wraps at
                 360, and the second line's text sat under the first link's
                 44px tap box and took its taps. Same words, same order; the
                 pieces are laid out as a wrapping row whose row gap is wider
                 than a tap box reaches, so no link has text under it. */
              <p
                className="nw-help flex flex-wrap items-center"
                style={{ columnGap: 4, rowGap: 24 }}
                data-testid="register-privacy-link"
              >
                <span>Read our</span>
                <TextLink as={Link} to="/privacy" state={{ from: location.pathname }} className="underline">
                  privacy notice
                </TextLink>
                <span>and</span>
                <TextLink as={Link} to="/terms" state={{ from: location.pathname }} className="underline" data-testid="register-terms-link">
                  terms of use
                </TextLink>
                <span>for what we hold and who can see it.</span>
              </p>
            ) : (
              <p className="nw-help" data-testid="register-privacy-link">
                Read our{" "}
                <TextLink as={Link} inline to="/privacy" state={{ from: location.pathname }} className="underline">
                  privacy notice
                </TextLink>{" "}
                for what we hold and who can see it.
              </p>
            )}

            {/* Issue #793 — this one never carried a local width override,
                and measurement (not assumption) showed it was already
                spanning: the immediate parent above is a flex column with
                no `align-self` override anywhere in this chain, so the
                default `align-items: stretch` was already giving this
                button its full column width before this issue's kit
                change. The change is a no-op HERE by design — see
                `kit/ActionButton.jsx`'s header. */}
            {createError && <CreateFailed message={createError} state={location.state} />}

            <ActionButton
              data-testid="register-submit"
              type="submit"
              disabled={busy || passkeyBusy || (guardianHeld && !guardianConfirmed)}
            >
              {busy ? "Creating…" : "Create account"}
            </ActionButton>
          </div>
        </form>

        {/* #714's passkey door creates a player, unconditionally, because
            `passkeys.ts` never carries a role (or #941's `roles` — see that
            schema's own `.omit`). Leaving this door open whenever Coach is
            ticked — coach-only OR both — would silently create the wrong
            account for a both-roles request too: the door has no way to
            grant the second hat, so it is hidden for BOTH cases (#941
            widens the condition from `coachOnly` to `coachChecked`) and
            replaced by the same plain sentence, client-side, rather than a
            change to the API. */}
        {passkeySupported && !coachChecked && (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <button
              type="button"
              onClick={signUpWithPasskey}
              disabled={busy || passkeyBusy || (guardianHeld && !guardianConfirmed)}
              className="nw-caption-strong"
              // #1394 (D221 G4, D354): a 44px tap box around the unchanged label.
              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--nw-ink-muted)", ...TAP_LINK_STYLE }}
              data-testid="register-passkey"
            >
              {passkeyBusy
                ? "Follow your browser's prompts…"
                : "Or create it with a passkey instead"}
            </button>
          </div>
        )}
        {passkeySupported && coachChecked && (
          <p className="nw-help" style={{ textAlign: "center" }} data-testid="register-passkey-coach-hidden">
            Creating a coach account with a passkey isn't available yet — use a password above.
          </p>
        )}

        <div className="nw-lede" style={{ textAlign: "center" }}>
          Already have an account?{" "}
          <Link
            to="/login"
            state={location.state}
            style={{ ...SIGN_IN_LINK_BOX, fontWeight: 700, color: "var(--nw-navy)" }}
            data-testid="link-login"
          >
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
